import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../../../components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { AppIcon } from '../../../components/ui/icon'
import { mdiFileExport, mdiFileImport, mdiDatabaseAlert } from '@mdi/js'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'

export const Route = createFileRoute('/$org/data/')({
  component: DataIndex,
})

function DataIndex() {
  const { org } = useParams({ from: '/$org/data/' })
  const { t } = useTranslation('data')

  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('breadcrumb'), path: `/${org}/data` }
  ])

  const dataFeatures = [
    {
      title: t('export.title'),
      description: t('export.description'),
      icon: mdiFileExport,
      href: `/${org}/data/export`,
      action: t('export.action'),
      testId: 'data-export-button',
    },
    {
      title: t('import.title'),
      description: t('import.description'),
      icon: mdiFileImport,
      href: `/${org}/data/import`,
      action: t('import.action'),
      testId: 'data-import-button',
    },
    // Data quality lives here rather than in Steering (spec 2026-08-24-03,
    // lot 10): fixing a conflicting value or a duplicate document is something
    // one does to the dataset, like importing and exporting it — not something
    // one calls a provider about.
    {
      title: t('quality.title'),
      description: t('quality.description'),
      icon: mdiDatabaseAlert,
      href: `/${org}/data/quality`,
      action: t('quality.action'),
      testId: 'data-quality-button',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        icon={SECTION_ICONS.data}
        title={t('page_title')}
        description={t('page_description')}
      />

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {dataFeatures.map((feature) => (
          <Card key={feature.title}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <AppIcon path={feature.icon} size="lg" className="text-primary" />
                {feature.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {feature.description}
              </p>
              <Button
                asChild
                className="w-full"
                data-testid={feature.testId}
              >
                <Link to={feature.href}>
                  {feature.action}
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
